import React, { useMemo } from "react"
import { Table, Tag, Typography } from "@mimber/ui-web"
import { useCommunicationTemplates } from "@mimber/client/mimber-organization"
import type { ColumnsType } from "@mimber/ui-web"
import type { CommunicationTemplateDto } from "@mimber/schema"
import {
  CommunicationChannel,
  CommunicationTemplateStatus,
} from "@mimber/schema"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  formatTableDate,
  getColumnSearchProps,
} from "@mimber/ui-web"
import type { CommunicationTemplatesTableProps } from "./CommunicationTemplatesTable.types"
import {
  CHANNEL_CONFIG,
  STATUS_CONFIG,
} from "./CommunicationTemplatesTable.utils"
import { CommunicationTemplateActionsDropdown } from "@/components/dropdowns"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
} from "@/utils"

const { Text } = Typography

export const CommunicationTemplatesTable: React.FunctionComponent<
  CommunicationTemplatesTableProps
> = ({ onEdit }) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "communication-templates",
      defaultQueryParams: {
        page: 1,
        limit: 20,
      },
    })

  const { data, isLoading } = useCommunicationTemplates(queryParams)

  const templates = data?.data ?? []
  const total = data?.meta?.pagination?.total ?? 0

  const columns: ColumnsType<CommunicationTemplateDto> = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        render: (name: string) => <Text strong>{name}</Text>,
        ...getColumnSearchProps("Search by name"),
      },
      {
        title: "Subject",
        dataIndex: "subject",
        key: "subject",
        ellipsis: true,
        render: (subject: string) => (
          <Text style={{ maxWidth: 300 }} ellipsis={{ tooltip: subject }}>
            {subject}
          </Text>
        ),
      },
      {
        title: "Channel",
        dataIndex: "channel",
        key: "channel",
        render: (channel: CommunicationChannel) => {
          const config = CHANNEL_CONFIG[channel]
          return (
            <Tag color={config.color} icon={config.icon}>
              {config.label}
            </Tag>
          )
        },
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status: CommunicationTemplateStatus) => {
          const config = STATUS_CONFIG[status]
          return <Tag color={config.color}>{config.label}</Tag>
        },
      },
      {
        title: "Created",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (date: string) => formatTableDate(date),
      },
      {
        title: "Updated",
        dataIndex: "updatedAt",
        key: "updatedAt",
        render: (date: string) => formatTableDate(date),
      },
      {
        title: "Actions",
        key: "actions",
        width: 60,
        onCell: () => ({
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
        }),
        render: (_: unknown, template: CommunicationTemplateDto) => (
          <CommunicationTemplateActionsDropdown
            template={template}
            onEdit={onEdit}
          />
        ),
      },
    ],
    [onEdit]
  )

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  return (
    <>
      <Table<CommunicationTemplateDto>
        bordered
        dataSource={templates}
        columns={displayColumns}
        loading={isLoading}
        rowKey="uuid"
        pagination={getRememberedPaginationConfig(
          total,
          queryParams,
          resetQueryParams
        )}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onRow={(record) => ({
          onClick: () => onEdit?.(record),
          style: onEdit ? { cursor: "pointer" } : undefined,
        })}
        onChange={(pagination, filters, sorter) => {
          const tableChangeData = {
            pagination,
            filters: filters as Record<string, string[] | null>,
            sorter: {
              field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
              order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
            } as { field?: string | string[]; order?: "ascend" | "descend" },
          }
          handleTableChange(tableChangeData, setQueryParams, columns)
        }}
      />
    </>
  )
}
